import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import LaunchIcon from "@material-ui/icons/Launch";

// Project images
import paymentManagement from "../assets/PMS.png";
import priceBookEditor from "../assets/PBE.png";
import workOrderManagement from "../assets/WOms.png";
import warehouseEfficiency from "../assets/WES.png";

const useStyles = makeStyles({
  root: {
    width: "100%",
    maxWidth: "100%",

    minHeight: "100vh",

    boxSizing: "border-box",

    background: "#353353",

    padding: "100px 7%",

    overflow: "hidden",
  },

  headingContainer: {
    maxWidth: 1200,

    margin: "0 auto 60px",
  },

  eyebrow: {
    display: "block",

    color: "#ff4f81",

    fontSize: 14,
    fontWeight: 700,

    textTransform: "uppercase",

    letterSpacing: 3,

    marginBottom: 8,
  },

  heading: {
    position: "relative",

    display: "inline-block",

    color: "#fff",

    fontSize: 45,
    fontWeight: 800,

    margin: 0,

    letterSpacing: -1,

    "&::after": {
      content: '""',

      position: "absolute",

      left: 0,
      bottom: -12,

      width: 55,
      height: 4,

      borderRadius: 10,

      background: "#ff0055",
    },
  },

  description: {
    maxWidth: 650,

    color: "#aaa9bb",

    fontSize: 15,

    lineHeight: 1.8,

    marginTop: 25,
  },

  projects: {
    width: "100%",
    maxWidth: 1200,

    margin: "0 auto",

    display: "grid",

    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",

    gap: 30,
  },

  project: {
    minWidth: 0,

    position: "relative",

    overflow: "hidden",

    borderRadius: 20,

    background: "rgba(255, 255, 255, 0.045)",

    border: "1px solid rgba(255, 255, 255, 0.08)",

    transition: "all 0.35s ease",

    "&:hover": {
      transform: "translateY(-8px)",

      borderColor: "rgba(255, 79, 129, 0.35)",

      boxShadow: "0 25px 55px rgba(0, 0, 0, 0.2)",
    },

    "&:hover $projectImage": {
      transform: "scale(1.05)",
    },

    "&:hover $imageOverlay": {
      opacity: 1,
    },
  },

  imageContainer: {
    position: "relative",

    width: "100%",

    height: 260,

    overflow: "hidden",

    background: "#292846",
  },

  projectImage: {
    width: "100%",
    height: "100%",

    display: "block",

    objectFit: "cover",

    transition: "transform 0.5s ease",
  },

  imageOverlay: {
    position: "absolute",

    inset: 0,

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    background: "rgba(30, 29, 55, 0.65)",

    opacity: 0,

    transition: "opacity 0.35s ease",
  },

  viewButton: {
    display: "inline-flex",

    alignItems: "center",

    gap: 7,

    padding: "10px 18px",

    borderRadius: 30,

    background: "#ff0055",

    color: "#fff",

    textDecoration: "none",

    fontSize: 13,

    fontWeight: 700,

    transition: "0.3s ease",

    "&:hover": {
      background: "#ff1767",

      transform: "translateY(-2px)",
    },
  },

  projectContent: {
    padding: "25px 25px 28px",
  },

  category: {
    display: "inline-block",

    color: "#ff4f81",

    fontSize: 11,

    fontWeight: 700,

    textTransform: "uppercase",

    letterSpacing: 1.5,

    marginBottom: 9,
  },

  projectTitle: {
    color: "#fff",

    fontSize: 23,

    fontWeight: 700,

    margin: "0 0 12px",
  },

  projectDescription: {
    color: "#aaa9bb",

    fontSize: 14,

    lineHeight: 1.75,

    margin: "0 0 20px",
  },

  technologies: {
    display: "flex",

    flexWrap: "wrap",

    gap: 7,
  },

  technology: {
    padding: "6px 10px",

    borderRadius: 20,

    background: "rgba(255, 255, 255, 0.06)",

    border: "1px solid rgba(255, 255, 255, 0.07)",

    color: "#c7c6d3",

    fontSize: 11,
  },

  bottom: {
    maxWidth: 1200,

    margin: "65px auto 0",

    padding: "25px 30px",

    borderRadius: 15,

    display: "flex",

    alignItems: "center",

    justifyContent: "space-between",

    gap: 20,

    background: "rgba(255, 255, 255, 0.04)",

    border: "1px solid rgba(255, 255, 255, 0.07)",
  },

  bottomText: {
    margin: 0,

    color: "#aaa9bb",

    fontSize: 14,
  },

  bottomStrong: {
    color: "#fff",

    fontWeight: 700,
  },

  moreButton: {
    display: "inline-flex",

    alignItems: "center",

    gap: 7,

    color: "#fff",

    textDecoration: "none",

    fontSize: 14,

    fontWeight: 600,

    whiteSpace: "nowrap",

    transition: "0.3s ease",

    "&:hover": {
      color: "#ff4f81",
    },
  },

  "@media (max-width: 900px)": {
    projects: {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },

  "@media (max-width: 650px)": {
    root: {
      padding: "70px 20px",
    },

    headingContainer: {
      marginBottom: 45,
    },

    heading: {
      fontSize: 36,
    },

    imageContainer: {
      height: 220,
    },

    projectContent: {
      padding: "22px 20px 25px",
    },

    projectTitle: {
      fontSize: 21,
    },

    bottom: {
      flexDirection: "column",

      textAlign: "center",

      padding: "25px 20px",

      marginTop: 45,
    },
  },
});

export default function Feed() {
  const classes = useStyles();

  const projects = [
    {
      title: "Payment Management",
      category: "Enterprise Application",

      image: paymentManagement,

      description:
        "A payment management application designed to streamline payment workflows while providing a clean, scalable and intuitive user experience.",

      tech: [
        "React",
        "TypeScript",
        "REST API",
        "UI/UX",
      ],

      link: "#",
    },

    {
      title: "Price Book Editor",
      category: "Business Application",

      image: priceBookEditor,

      description:
        "A modern price book management platform focused on usability, reusable components, efficient data management and a responsive interface.",

      tech: [
        "React",
        "TypeScript",
        "Material UI",
        "API",
      ],

      link: "#",
    },

    {
      title: "Work Order Management",
      category: "Enterprise Application",

      image: workOrderManagement,

      description:
        "A scalable work order management system for managing jobs, schedules, workflows and operational data across business processes.",

      tech: [
        "React",
        "Node.js",
        "REST API",
        "Database",
      ],

      link: "#",
    },

    {
      title: "Warehouse Efficiency System",
      category: "Warehouse Solution",

      image: warehouseEfficiency,

      description:
        "A warehouse management solution focused on improving operational efficiency, visibility, inventory workflows and day-to-day operations.",

      tech: [
        "React",
        "TypeScript",
        "Node.js",
        "API",
      ],

      link: "#",
    },
  ];

  return (
    <section id="work" className={classes.root}>

      {/* Heading */}
      <div className={classes.headingContainer}>

        <span className={classes.eyebrow}>
          Portfolio
        </span>

        <h1 className={classes.heading}>
          Recent Work
        </h1>

        <p className={classes.description}>
          A selection of projects I've worked on, ranging from
          enterprise applications and business platforms to
          custom web solutions.
        </p>

      </div>


      {/* Projects */}
      <div className={classes.projects}>

        {projects.map((project) => (
          <article
            className={classes.project}
            key={project.title}
          >

            {/* Image */}
            <div className={classes.imageContainer}>

              <img
                src={project.image}
                alt={project.title}
                className={classes.projectImage}
              />

              {/* Hover overlay */}
              <div className={classes.imageOverlay}>

                <a
                  href={project.link}
                  className={classes.viewButton}
                >
                  View Project
                  <LaunchIcon style={{ fontSize: 16 }} />
                </a>

              </div>

            </div>


            {/* Content */}
            <div className={classes.projectContent}>

              <span className={classes.category}>
                {project.category}
              </span>

              <h2 className={classes.projectTitle}>
                {project.title}
              </h2>

              <p className={classes.projectDescription}>
                {project.description}
              </p>


              {/* Technologies */}
              <div className={classes.technologies}>

                {project.tech.map((technology) => (
                  <span
                    key={technology}
                    className={classes.technology}
                  >
                    {technology}
                  </span>
                ))}

              </div>

            </div>

          </article>
        ))}

      </div>

    </section>
  );
}